import * as React from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";
import * as SelectPrimitive from "@radix-ui/react-select";

import { cn } from "@/lib/utils";
import { RiArrowDownSLine } from "@remixicon/react";

const SqSelect = SelectPrimitive.Root;

const SqSelectGroup = SelectPrimitive.Group;

const SqSelectValue = SelectPrimitive.Value;

interface SqSelectTriggerProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> {
  chevronClassName?: string;
  bgColor?: string;
  outlineColor?: string;
  hoverBgColor?: string;
  hoverOutlineColor?: string;
  radius?: number;
  shadow?: "none" | "sm" | "md" | "lg" | "xl";
  showOutline?: boolean;
}

const SqSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SqSelectTriggerProps
>(
  (
    {
      className,
      children,
      chevronClassName = "",
      shadow,
      bgColor,
      outlineColor,
      hoverBgColor,
      hoverOutlineColor,
      radius = 8,
      style,
      showOutline = true,
      ...props
    },
    ref,
  ) => {
    // Map custom colors into CSS variables, then bind those to squircle painter vars via
    // Tailwind arbitrary properties (with !important to override variant defaults).
    const customCssVars = {
      ...(style || {}),
      "--sq-radius": radius ? `${radius}px` : "8px",
      "--sq-outline-width": "1px",
      "--sq-outline-color": "#E7E8E9",
      "--sq-white": "white",
      "--sq-hover-gray": "#FBFBFB",
      ...(bgColor && { "--sq-fill": bgColor }),
      ...(hoverBgColor && { "--sq-hover-fill": hoverBgColor }),
      ...(outlineColor && { "--sq-outline": outlineColor }),
      ...(hoverOutlineColor && { "--sq-hover-outline": hoverOutlineColor }),
    } as React.CSSProperties;

    const colorBindingClasses = cn(
      bgColor && "squircle-fill-[var(--sq-fill)]",
      hoverBgColor && "hover:squircle-fill-[var(--sq-hover-fill)]",
      showOutline && "before:squircle-outline-[1px]",
      showOutline && outlineColor && "before:squircle-outline-color-[var(--sq-outline)]",
      showOutline && !outlineColor && "before:squircle-outline-color-[#E7E8E9]",
      showOutline &&
        hoverOutlineColor &&
        "hover:before:squircle-outline-color-[var(--sq-hover-outline)]",
    );

    const radiusClasses = cn(
      "squircle-radius-[var(--sq-radius)]",
      showOutline && "before:squircle-radius-[var(--sq-radius)]",
    );

    const shadowClasses = cn(
      (shadow === "sm" || shadow === undefined) && "drop-shadow-sm",
      shadow === "md" && "drop-shadow-md",
      shadow === "lg" && "drop-shadow-lg",
      shadow === "xl" && "drop-shadow-xl",
    );

    const outlineClasses = cn(
      showOutline &&
        "relative before:absolute before:inset-0 before:squircle before:squircle-radius-[var(--sq-radius)] before:squircle-outline-[1px] before:squircle-outline-color-[#E7E8E9] before:pointer-events-none",
    );

    return (
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          "squircle squircle-radius-[var(--sq-radius)] squircle-smooth-[1] squircle-fill-[var(--sq-white)] transition-all hover:squircle-fill-[var(--sq-hover-gray)]",
          shadowClasses,
          outlineClasses,
          colorBindingClasses,
          radiusClasses,
          "shadow-none bg-transparent flex h-8 items-center justify-between whitespace-nowrap px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 outline-none",
          className,
        )}
        style={customCssVars}
        {...props}
      >
        {children}
        <SelectPrimitive.Icon asChild>
          <RiArrowDownSLine
            size={14}
            className={cn("shrink-0 text-[#9aa3bd]", chevronClassName)}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    );
  },
);
SqSelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SqSelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpButton>
));
SqSelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SqSelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
));
SqSelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

interface SqSelectContentProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Content
> {
  position?: "popper" | "item-aligned";
  bgColor?: string;
  outlineColor?: string;
  radius?: number;
  shadow?: "none" | "sm" | "md" | "lg" | "xl";
  showScrollUpDown?: boolean;
}

const SqSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SqSelectContentProps
>(
  (
    {
      className,
      children,
      position = "popper",
      shadow,
      bgColor,
      outlineColor,
      radius = 12,
      style,
      showScrollUpDown,
      ...props
    },
    ref,
  ) => {
    const customCssVars = {
      ...(style || {}),
      ...(bgColor ? { "--sq-fill": bgColor } : {}),
      ...(outlineColor ? { "--sq-outline": outlineColor } : {}),
      "--sq-radius": `${radius}px`,
    } as React.CSSProperties & Record<string, string>;

    const colorBindingClasses = cn(
      bgColor && "squircle-fill-[var(--sq-fill)]",
      outlineColor && "before:squircle-outline-color-[var(--sq-outline)]",
    );

    const radiusClasses = cn(
      "squircle-radius-[var(--sq-radius)]",
      "before:squircle-radius-[var(--sq-radius)]",
    );

    const shadowClasses = cn(
      shadow === "sm" && "drop-shadow-sm",
      shadow === "md" && "drop-shadow-md",
      shadow === "lg" && "drop-shadow-lg",
      shadow === "xl" && "drop-shadow-xl",
    );

    const outlineClasses = cn(
      "relative before:absolute before:inset-0 before:squircle before:squircle-radius-[var(--sq-radius)] before:squircle-outline-[1px] before:squircle-outline-color-border-gray before:pointer-events-none",
    );

    return (
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "squircle squircle-radius-[12px] squircle-smooth-[1] squircle-fill-[white] transition-all",
          shadowClasses,
          outlineClasses,
          colorBindingClasses,
          radiusClasses,
          "relative z-50 max-h-96 min-w-[8rem] overflow-hidden px-[1px] py-[1px] text-popover-foreground drop-shadow-md",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        style={customCssVars}
        position={position}
        {...props}
      >
        {showScrollUpDown && <SqSelectScrollUpButton />}
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        {showScrollUpDown && <SqSelectScrollDownButton />}
      </SelectPrimitive.Content>
    );
  },
);
SqSelectContent.displayName = SelectPrimitive.Content.displayName;

const SqSelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props}
  />
));
SqSelectLabel.displayName = SelectPrimitive.Label.displayName;

interface SqSelectItemProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Item
> {
  hideItemIndicator?: boolean;
  bgColor?: string;
  hoverBgColor?: string;
  radius?: number;
}

const SqSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SqSelectItemProps
>(
  (
    {
      className,
      children,
      hideItemIndicator = true,
      bgColor,
      hoverBgColor,
      radius = 6,
      style,
      ...props
    },
    ref,
  ) => {
    // Map custom colors into CSS variables, then bind those to squircle painter vars via
    // Tailwind arbitrary properties (with !important to override variant defaults).
    const customCssVars = {
      ...(style || {}),
      ...(bgColor ? { "--sq-fill": bgColor } : {}),
      ...(hoverBgColor ? { "--sq-hover-fill": hoverBgColor } : {}),
      "--sq-radius": `${radius}px`,
    } as React.CSSProperties & Record<string, string>;

    const colorBindingClasses = cn(
      bgColor && "squircle-fill-[var(--sq-fill)]",
      hoverBgColor &&
        "hover:squircle-fill-[var(--sq-hover-fill)] focus:squircle-fill-[var(--sq-hover-fill)]",
    );

    const radiusClasses = cn("squircle-radius-[var(--sq-radius)]");

    return (
      <SelectPrimitive.Item
        ref={ref}
        className={cn(
          "h-7 relative flex w-full cursor-pointer select-none items-center squircle squircle-radius-[6px] py-1.5 pl-2 text-[12px] outline-none hover:squircle-fill-hover-gray data-[state=checked]:squircle-fill-selected-gray data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          hideItemIndicator && "pr-2",
          colorBindingClasses,
          radiusClasses,
          className,
        )}
        style={customCssVars}
        {...props}
      >
        {children}
      </SelectPrimitive.Item>
    );
  },
);
SqSelectItem.displayName = SelectPrimitive.Item.displayName;

const SqSelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SqSelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  SqSelect,
  SqSelectGroup,
  SqSelectValue,
  SqSelectTrigger,
  SqSelectContent,
  SqSelectLabel,
  SqSelectItem,
  SqSelectSeparator,
  SqSelectScrollUpButton,
  SqSelectScrollDownButton,
};
